/**
 * 提示词库页面
 */

import React, { useState, useEffect } from 'react';
import { 
  Prompt, 
  PromptInput, 
  PromptListQuery, 
  PromptListResponse, 
  PromptService,
  PromptAPIError 
} from '../../services/prompts';
import PromptCard from '../PromptCard';
import PromptForm from '../forms/PromptForm';
import PromptDetailModal from '../PromptDetailModal';
import Toast from '../ui/Toast';
import { PROMPT_CATEGORIES } from '../../utils/categories';

interface PromptsPageProps {
  autoCreate?: boolean;
  onNavigate?: (page: string) => void;
}

export const PromptsPage: React.FC<PromptsPageProps> = ({ autoCreate = false, onNavigate }) => {
  // 状态管理
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(autoCreate);
  const [editingPrompt, setEditingPrompt] = useState<Prompt | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [viewingPrompt, setViewingPrompt] = useState<Prompt | null>(null);
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info'; isVisible: boolean }>({
    message: '',
    type: 'success',
    isVisible: false
  });

  // 搜索和分页状态
  const [searchTerm, setSearchTerm] = useState('');
  const [category, setCategory] = useState('');
  const [sortBy, setSortBy] = useState<'createdAt' | 'updatedAt' | 'title' | 'usageCount'>('updatedAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);

  // 成功消息状态
  const [successMessage, _setSuccessMessage] = useState<string | null>(null);
  
  // 多选和批量操作
  const [selectedPrompts, setSelectedPrompts] = useState<Set<string>>(new Set());
  const [showBatchActions, setShowBatchActions] = useState(false);
  
  // 每页显示数量
  const [pageSize, setPageSize] = useState(12);

  // 加载提示词数据
  const loadPrompts = async () => {
    try {
      setLoading(true);
      setError(null);

      const query: PromptListQuery = {
        page: currentPage,
        limit: pageSize,
        search: searchTerm || undefined,
        category: category || undefined,
        sortBy,
        sortOrder,
      };

      const response: PromptListResponse & { message: string } = await PromptService.getPrompts(query);
      
      // 如果只看收藏，客户端筛选
      let filteredPrompts = response.prompts;
      if (showFavoritesOnly) {
        filteredPrompts = response.prompts.filter(p => p.isFavorite);
      }
      
      setPrompts(filteredPrompts);
      setTotal(showFavoritesOnly ? filteredPrompts.length : response.total);
      setHasNextPage(showFavoritesOnly ? false : response.hasNextPage);
    } catch (err) {
      const error = err as PromptAPIError;
      setError(error.message || '加载提示词失败');
    } finally {
      setLoading(false);
    }
  };

  // 页面加载时获取数据
  useEffect(() => {
    loadPrompts();
  }, [currentPage, searchTerm, category, sortBy, sortOrder, showFavoritesOnly, pageSize]);

  // 显示Toast消息
  const showToast = (message: string, type: 'success' | 'error' | 'info' = 'success') => {
    setToast({ message, type, isVisible: true });
  };

  // 创建提示词
  const handleCreatePrompt = async (promptInput: PromptInput) => {
    try {
      setSubmitting(true);
      const response = await PromptService.createPrompt(promptInput);
      showToast(response.message);
      setShowCreateForm(false);
      setEditingPrompt(null); // 清除编辑状态（克隆时会用到）
      loadPrompts(); // 重新加载列表
    } catch (err) {
      const error = err as PromptAPIError;
      throw new Error(error.message || '创建提示词失败');
    } finally {
      setSubmitting(false);
    }
  };

  // 编辑提示词
  const handleEditPrompt = async (promptInput: PromptInput) => {
    if (!editingPrompt) return;

    try {
      setSubmitting(true);
      const response = await PromptService.updatePrompt(editingPrompt.id, promptInput);
      showToast(response.message);
      setEditingPrompt(null);
      loadPrompts(); // 重新加载列表
    } catch (err) {
      const error = err as PromptAPIError;
      throw new Error(error.message || '更新提示词失败');
    } finally {
      setSubmitting(false);
    }
  };

  // 删除提示词
  const handleDeletePrompt = async (promptId: string) => {
    try {
      const response = await PromptService.deletePrompt(promptId);
      showToast(response.message);
      loadPrompts(); // 重新加载列表
    } catch (err) {
      const error = err as PromptAPIError;
      setError(error.message || '删除提示词失败');
    }
  };

  // 使用提示词（增加使用次数）
  const handleUsePrompt = async (promptId: string) => {
    try {
      await PromptService.usePrompt(promptId);
      // 静默更新，不需要重新加载整个列表
      const updatedPrompts = prompts.map(p => 
        p.id === promptId ? { ...p, usageCount: p.usageCount + 1 } : p
      );
      setPrompts(updatedPrompts);
      
      // 如果弹窗正在显示这个提示词，也更新弹窗数据
      if (viewingPrompt && viewingPrompt.id === promptId) {
        setViewingPrompt({ ...viewingPrompt, usageCount: viewingPrompt.usageCount + 1 });
      }
    } catch (err) {
      console.error('增加使用次数失败:', err);
    }
  };

  // 查看提示词详情（增加查看次数）
  const handleViewPrompt = async (prompt: Prompt) => {
    // 先设置弹窗显示
    setViewingPrompt(prompt);
    
    try {
      await PromptService.viewPrompt(prompt.id);
      // 静默更新查看次数
      const updatedPrompt = { ...prompt, viewCount: (prompt.viewCount || 0) + 1 };
      setPrompts(prev => prev.map(p => 
        p.id === prompt.id ? updatedPrompt : p
      ));
      // 更新当前查看的提示词数据
      setViewingPrompt(updatedPrompt);
    } catch (err) {
      console.error('增加查看次数失败:', err);
    }
  };

  // 克隆提示词
  const handleClonePrompt = (prompt: Prompt) => {
    // 创建克隆数据
    const clonedPrompt: Prompt = {
      ...prompt,
      id: '', // 新提示词没有ID
      title: `${prompt.title} (副本)`,
      createdAt: Date.now(),
      updatedAt: Date.now(),
      usageCount: 0,
      viewCount: 0,
      isFavorite: false, // 新提示词默认不收藏
    };
    
    // 设置为创建模式，预填充克隆的数据
    setEditingPrompt(clonedPrompt);
  };

  // 切换收藏状态
  const handleToggleFavorite = async (promptId: string, isFavorite: boolean) => {
    try {
      const response = await PromptService.toggleFavorite(promptId, isFavorite);
      showToast(isFavorite ? '已加入收藏' : '已取消收藏');
      // 更新本地状态
      setPrompts(prev => prev.map(p => 
        p.id === promptId ? response.prompt : p
      ));
      // 如果正在查看详情，也更新详情数据
      if (viewingPrompt && viewingPrompt.id === promptId) {
        setViewingPrompt(response.prompt);
      }
    } catch (err) {
      const error = err as PromptAPIError;
      setError(error.message || '操作失败');
    }
  };

  // 重置搜索
  const resetFilters = () => {
    setSearchTerm('');
    setCategory('');
    setShowFavoritesOnly(false);
    setCurrentPage(1);
  };

  // 多选功能
  const handleSelectPrompt = (promptId: string, isSelected: boolean) => {
    const newSelected = new Set(selectedPrompts);
    if (isSelected) {
      newSelected.add(promptId);
    } else {
      newSelected.delete(promptId);
    }
    setSelectedPrompts(newSelected);
  };

  const handleSelectAll = (isSelected: boolean) => {
    if (isSelected) {
      const allIds = new Set(prompts.map(p => p.id));
      setSelectedPrompts(allIds);
    } else {
      setSelectedPrompts(new Set());
    }
  };

  // 批量删除
  const handleBatchDelete = async () => {
    if (selectedPrompts.size === 0) return;
    
    if (!window.confirm(`确定要删除选中的 ${selectedPrompts.size} 个提示词吗？此操作不可撤销。`)) {
      return;
    }

    try {
      // 串行删除以避免并发问题
      for (const promptId of selectedPrompts) {
        await PromptService.deletePrompt(promptId);
      }
      
      showToast(`成功删除 ${selectedPrompts.size} 个提示词`);
      setSelectedPrompts(new Set());
      setShowBatchActions(false);
      loadPrompts();
    } catch (err) {
      const error = err as PromptAPIError;
      setError(error.message || '批量删除失败');
    }
  };

  return (
    <div className="space-y-6">
      {/* 成功消息 */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-md p-4">
          <div className="flex">
            <svg className="h-5 w-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <div className="ml-3">
              <p className="text-sm text-green-800">{successMessage}</p>
            </div>
          </div>
        </div>
      )}

      {/* 错误消息 */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <div className="ml-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
            <button 
              onClick={() => setError(null)}
              className="ml-auto pl-3"
            >
              <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* 创建/编辑表单 */}
      {(showCreateForm || editingPrompt) && (
        <PromptForm
          prompt={editingPrompt || undefined}
          isEditing={!!editingPrompt && !!editingPrompt.id}
          onSubmit={(!!editingPrompt && !!editingPrompt.id) ? handleEditPrompt : handleCreatePrompt}
          onCancel={() => {
            setShowCreateForm(false);
            setEditingPrompt(null);
          }}
          loading={submitting}
        />
      )}

      {/* 页面标题和操作栏 */}
      {!showCreateForm && !editingPrompt && (
        <>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">提示词库</h1>
              <p className="mt-1 text-sm text-gray-500">
                管理和使用您的个人提示词收藏 ({total} 个提示词)
              </p>
            </div>
            <div className="mt-4 sm:mt-0 flex items-center space-x-3">
              {prompts.length > 0 && (
                <button 
                  onClick={() => {
                    setShowBatchActions(!showBatchActions);
                    setSelectedPrompts(new Set());
                  }}
                  className={`inline-flex items-center px-3 py-2 border text-sm font-medium rounded-md transition-colors ${
                    showBatchActions
                      ? 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
                      : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
                  }`}
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {showBatchActions ? '退出批量' : '批量操作'}
                </button>
              )}
              
              {onNavigate && (
                <button 
                  onClick={() => onNavigate('import')}
                  className="inline-flex items-center px-4 py-2 border border-green-300 text-sm font-medium rounded-md text-green-700 bg-green-50 hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  批量导入
                </button>
              )}
              
              <button 
                onClick={() => setShowCreateForm(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                创建提示词
              </button>
            </div>
          </div>

          {/* 搜索和筛选 */}
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <div className="space-y-4">
              {/* 搜索框、排序和收藏筛选 */}
              <div className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
                <div className="flex-1 max-w-lg">
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                    <input
                      type="text"
                      placeholder="搜索提示词标题、内容或标签..."
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1);
                      }}
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                {/* 排序选择和收藏筛选 */}
                <div className="flex items-center space-x-3">
                  <select
                    value={`${sortBy}-${sortOrder}`}
                    onChange={(e) => {
                      const [newSortBy, newSortOrder] = e.target.value.split('-') as [typeof sortBy, typeof sortOrder];
                      setSortBy(newSortBy);
                      setSortOrder(newSortOrder);
                      setCurrentPage(1);
                    }}
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="updatedAt-desc">最近更新</option>
                    <option value="createdAt-desc">最近创建</option>
                    <option value="title-asc">标题 A-Z</option>
                    <option value="usageCount-desc">使用次数</option>
                    <option value="viewCount-desc">查看次数</option>
                  </select>

                  <button
                    onClick={() => {
                      setShowFavoritesOnly(!showFavoritesOnly);
                      setCurrentPage(1);
                    }}
                    className={`inline-flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors flex-shrink-0 ${
                      showFavoritesOnly
                        ? 'bg-yellow-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <span className="mr-1">⭐</span>
                    仅看收藏
                  </button>
                </div>
              </div>

              {/* 分类筛选 - 平铺展示 */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">业务分类</h3>
                <div className="flex flex-wrap gap-2">
                  {/* 全部分类按钮 */}
                  <button
                    onClick={() => {
                      setCategory('');
                      setCurrentPage(1);
                    }}
                    className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                      !category
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <span className="mr-1">📝</span>
                    全部分类
                  </button>
                  
                  {/* 各分类按钮 */}
                  {PROMPT_CATEGORIES.map((cat) => (
                    <button
                      key={cat.value}
                      onClick={() => {
                        setCategory(cat.value === category ? '' : cat.value);
                        setCurrentPage(1);
                      }}
                      className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                        category === cat.value
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      <span className="mr-1">{cat.icon}</span>
                      {cat.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* 筛选信息和重置 */}
              {(searchTerm || category || showFavoritesOnly) && (
                <div className="flex items-center justify-between text-sm pt-2 border-t border-gray-100">
                  <span className="text-gray-500">
                    {searchTerm && `搜索: "${searchTerm}"`}
                    {searchTerm && (category || showFavoritesOnly) && ' • '}
                    {category && `分类: "${category}"`}
                    {category && showFavoritesOnly && ' • '}
                    {showFavoritesOnly && '仅看收藏'}
                  </span>
                  <button
                    onClick={resetFilters}
                    className="text-blue-600 hover:text-blue-800 font-medium"
                  >
                    清除筛选
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* 批量操作栏 */}
          {showBatchActions && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={selectedPrompts.size === prompts.length && prompts.length > 0}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      全选 ({selectedPrompts.size}/{prompts.length})
                    </span>
                  </label>
                  
                  {selectedPrompts.size > 0 && (
                    <span className="text-sm text-blue-600">
                      已选择 {selectedPrompts.size} 个提示词
                    </span>
                  )}
                </div>
                
                <div className="flex items-center space-x-2">
                  {selectedPrompts.size > 0 && (
                    <button
                      onClick={handleBatchDelete}
                      className="inline-flex items-center px-3 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      批量删除
                    </button>
                  )}
                  
                  <button
                    onClick={() => {
                      setShowBatchActions(false);
                      setSelectedPrompts(new Set());
                    }}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    退出批量
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* 加载状态 */}
          {loading && (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          )}

          {/* 提示词列表 */}
          {!loading && (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {prompts.length > 0 ? (
                prompts.map((prompt) => (
                  <PromptCard
                    key={prompt.id}
                    prompt={prompt}
                    onEdit={setEditingPrompt}
                    onDelete={handleDeletePrompt}
                    onUse={handleUsePrompt}
                    onToggleFavorite={handleToggleFavorite}
                    onViewDetail={handleViewPrompt}
                    onClone={handleClonePrompt}
                    isBatchMode={showBatchActions}
                    isSelected={selectedPrompts.has(prompt.id)}
                    onSelect={handleSelectPrompt}
                  />
                ))
              ) : (
                <div className="col-span-full text-center py-12">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <h3 className="mt-2 text-sm font-medium text-gray-900">暂无提示词</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    {searchTerm || category ? '没有找到匹配的提示词' : '开始创建您的第一个提示词吧'}
                  </p>
                  {!searchTerm && !category && (
                    <button
                      onClick={() => setShowCreateForm(true)}
                      className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                    >
                      创建提示词
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* 分页 */}
          {!loading && prompts.length > 0 && (
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-500">
                第 {currentPage} 页，共 {Math.ceil(total / pageSize)} 页 • 总计 {total} 个提示词
              </div>
              <div className="flex items-center space-x-3">
                <div className="flex space-x-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    上一页
                  </button>
                  <button
                    onClick={() => setCurrentPage(prev => prev + 1)}
                    disabled={!hasNextPage}
                    className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    下一页
                  </button>
                </div>
                
                {/* 每页显示数量选择 */}
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white"
                >
                  <option value={6}>每页 6 个</option>
                  <option value={12}>每页 12 个</option>
                  <option value={24}>每页 24 个</option>
                  <option value={48}>每页 48 个</option>
                </select>
              </div>
            </div>
          )}
        </>
      )}

      {/* 提示词详情弹窗 */}
      <PromptDetailModal
        prompt={viewingPrompt}
        isOpen={!!viewingPrompt}
        onClose={() => setViewingPrompt(null)}
        onEdit={(prompt) => {
          setEditingPrompt(prompt);
          setViewingPrompt(null);
        }}
        onUse={handleUsePrompt}
        onToggleFavorite={handleToggleFavorite}
        onClone={(prompt) => {
          handleClonePrompt(prompt);
          setViewingPrompt(null);
        }}
      />

      {/* Toast 通知 */}
      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onHide={() => setToast(prev => ({ ...prev, isVisible: false }))}
      />
    </div>
  );
}; 